/**
 * Normalisation for the Onboarding surface (spec §9.4, §10.4).
 *
 * Three unrelated backends feed this screen and none of them is shaped like a
 * "path with chapters":
 *
 *   - `GET /api/learning/paths(/:id)` — admin-assigned curricula, chaptered as
 *     ordered deck modules. These are assignments, so they are the required
 *     part, and they are the ONE place Etch is allowed to create learning
 *     objects without the user confirming each one (spec §9.2).
 *   - `GET /api/missions?status=onboarding` — mission-backed steps.
 *   - `GET /api/ckb/onboarding-plan` — an LLM plan over the connected
 *     knowledge source. Nobody assigned it, so it is voluntary depth.
 *
 * Everything here is pure: same inputs → same path objects, no fetching, no
 * storage. That keeps the completion rules readable in one place, which
 * matters because "completed" is a claim about a person (spec §3.3 point 4:
 * transfer, not activity).
 *
 * COMPLETION RULES — deliberately conservative:
 *   mission step   → mastery level is `practiced` or `secure`. Attempting a
 *                    mission is activity; reaching `practiced` is evidence.
 *   question step  → every card in the module's deck has left the `new` state.
 *   content step   → self-reported (the user ticks it). There is no server-side
 *                    signal for "has read this", and inventing one would be a
 *                    completion claim the product cannot back up.
 *   practice step  → self-reported, same reason.
 */

/** The four step kinds a chapter mixes (spec §9.4). */
export const STEP_KINDS = ['content', 'question', 'mission', 'practice'];

/** Mastery levels that count a mission step as done. */
const MASTERED_LEVELS = ['practiced', 'secure'];

/** Category order of the generated plan — chapters render in this order. */
export const CATEGORY_ORDER = ['overview', 'concepts', 'entrypoints', 'modules', 'connections'];

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/* -------------------------------------------------------------------------- */
/* Steps                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Which of the four kinds a generated plan step is. The plan's `action.type`
 * is the only honest signal available; a step the planner gave no action to is
 * something the developer has to go and do, i.e. a practical task.
 */
export function kindFromAction(action) {
  switch (action?.type) {
    case 'file':
    case 'explore':
    case 'graph':
      return 'content';
    case 'chat':
      return 'question';
    default:
      return 'practice';
  }
}

/** Deep link for a generated plan step. Router path + optional nav intent. */
function linkFromAction(action) {
  const target = typeof action?.target === 'string' ? action.target.trim() : '';
  switch (action?.type) {
    case 'graph':
      return {
        to: '/knowledge',
        intent: { path: '/knowledge', data: { tab: 'graph', vizType: 'files' } },
        actionKey: 'onboarding.actions.viewGraph',
      };
    case 'file':
    case 'explore':
      if (!target) return null;
      return {
        to: '/knowledge',
        intent: { path: '/knowledge', data: { file: target } },
        actionKey: action.type === 'file' ? 'onboarding.actions.viewFile' : 'onboarding.actions.explore',
      };
    case 'chat':
      if (!target) return null;
      return {
        to: '/chat',
        intent: { path: '/chat', data: { question: target } },
        actionKey: 'onboarding.actions.askQuestion',
      };
    default:
      return null;
  }
}

function missionStep(mission, { required, prefix }) {
  const level = mission?.mastery?.level || 'new';
  const attempts = num(mission?.mastery?.attempts);
  return {
    id: `${prefix}mission:${mission.id}`,
    missionId: mission.id,
    title: mission.title,
    description: mission.scenario || null,
    kind: 'mission',
    required,
    manual: false,
    completed: MASTERED_LEVELS.includes(level),
    evidence: MASTERED_LEVELS.includes(level) ? 'mastery' : null,
    masteryLevel: level,
    to: `/missions/run/${mission.id}`,
    intent: null,
    actionKey: attempts > 0 ? 'onboarding.actions.continueMission' : 'onboarding.actions.startMission',
  };
}

/* -------------------------------------------------------------------------- */
/* Paths                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * An admin-assigned curriculum.
 *
 * `detail` is `{status:'ready'|'failed', modules}` or undefined while the
 * per-path chapter request is still in flight. The three cases are kept apart
 * on purpose: a failed detail must render as a failure the user can retry, not
 * as an endless spinner and not as an empty path (which would read as "you
 * have nothing to do" — the worst possible lie on an assigned path).
 */
export function buildAssignedPath({ summary, detail, missions = [], completed = {} }) {
  const moduleRows = Array.isArray(detail?.modules) ? detail.modules : [];
  const byDeck = new Map();
  for (const mission of missions) {
    if (!mission?.deckId) continue;
    if (!byDeck.has(mission.deckId)) byDeck.set(mission.deckId, []);
    byDeck.get(mission.deckId).push(mission);
  }

  const chapters = moduleRows.map((module, index) => {
    const prefix = `path:${summary.id}:module:${module.id}:`;
    const cardCount = num(module.card_count);
    const learned = num(module.learned_count);
    const steps = [];

    if (module.description) {
      steps.push({
        id: `${prefix}content`,
        title: module.title || module.deck_title || '',
        description: module.description,
        kind: 'content',
        required: true,
        manual: true,
        completed: Boolean(completed[`${prefix}content`]),
        evidence: completed[`${prefix}content`] ? 'self' : null,
        to: null,
        intent: null,
        actionKey: null,
      });
    }

    if (cardCount > 0) {
      steps.push({
        id: `${prefix}questions`,
        title: module.deck_title || module.title || '',
        description: null,
        kind: 'question',
        required: true,
        manual: false,
        completed: learned >= cardCount,
        evidence: learned >= cardCount ? 'review' : null,
        counts: { learned, total: cardCount, due: num(module.due_count) },
        to: `/missions/review/${module.deck_id}`,
        intent: null,
        actionKey: 'onboarding.actions.answerQuestions',
      });
    }

    for (const mission of byDeck.get(module.deck_id) || []) {
      steps.push(missionStep(mission, { required: true, prefix }));
    }

    return {
      id: module.id,
      index: index + 1,
      title: module.title || module.deck_title || '',
      description: module.description || null,
      steps,
    };
  });

  return withProgress({
    id: String(summary.id),
    kind: 'assigned',
    title: summary.name || '',
    description: summary.description || null,
    icon: summary.icon || null,
    assigned: true,
    required: true,
    detailLoaded: detail?.status === 'ready',
    detailFailed: detail?.status === 'failed',
    // Summary counts stand in for real progress until the detail arrives.
    summaryCounts: {
      modules: num(summary.module_count),
      cards: num(summary.total_cards),
      learned: num(summary.learned_cards),
      due: num(summary.due_count),
    },
    chapters,
  });
}

/**
 * The voluntary path planned over the connected knowledge source. Steps are
 * grouped into chapters by the planner's own `category`, which is also what
 * the pre-Etch panel did — the difference is that a chapter now carries a mix
 * of kinds instead of a flat checklist.
 *
 * `missions` are matched to plan steps through the mission's `sourceRef`, the
 * only link the mission author writes back. Unmatched onboarding missions are
 * NOT smuggled in here; they get their own path (`buildMissionPath`).
 */
export function buildGeneratedPath({ steps, missions = [], completed = {} }) {
  const planSteps = Array.isArray(steps) ? steps : [];
  if (planSteps.length === 0) return null;

  const byStepId = new Map();
  for (const mission of missions) {
    const stepId = mission?.sourceRef?.stepId || mission?.sourceRef?.onboardingStepId;
    if (!stepId) continue;
    if (!byStepId.has(stepId)) byStepId.set(stepId, []);
    byStepId.get(stepId).push(mission);
  }

  const grouped = new Map();
  for (const planStep of planSteps) {
    const category = CATEGORY_ORDER.includes(planStep?.category) ? planStep.category : 'overview';
    if (!grouped.has(category)) grouped.set(category, []);
    grouped.get(category).push(planStep);
  }

  const chapters = [];
  for (const category of CATEGORY_ORDER) {
    const items = grouped.get(category);
    if (!items || items.length === 0) continue;

    const steps = [];
    for (const planStep of items) {
      const id = `plan:${planStep.id}`;
      const link = linkFromAction(planStep.action);
      steps.push({
        id,
        title: planStep.title || '',
        description: planStep.description || null,
        kind: kindFromAction(planStep.action),
        required: false,
        manual: true,
        completed: Boolean(completed[id]),
        evidence: completed[id] ? 'self' : null,
        to: link?.to || null,
        intent: link?.intent || null,
        actionKey: link?.actionKey || null,
      });
      for (const mission of byStepId.get(planStep.id) || []) {
        steps.push(missionStep(mission, { required: false, prefix: `plan:${planStep.id}:` }));
      }
    }

    chapters.push({
      id: category,
      index: chapters.length + 1,
      titleKey: `onboarding.categories.${category}`,
      title: null,
      description: null,
      steps,
    });
  }

  return withProgress({
    id: 'generated',
    kind: 'generated',
    titleKey: 'onboarding.generatedPathTitle',
    descriptionKey: 'onboarding.generatedPathDescription',
    assigned: false,
    required: false,
    detailLoaded: true,
    detailFailed: false,
    chapters,
  });
}

/**
 * Onboarding missions that belong to no assigned module and no plan step.
 * They were still assigned as onboarding, so they stay in the required half
 * rather than quietly disappearing.
 */
export function buildMissionPath({ missions, claimedIds }) {
  const leftovers = (missions || []).filter((mission) => !claimedIds.has(mission.id));
  if (leftovers.length === 0) return null;

  return withProgress({
    id: 'onboarding-missions',
    kind: 'missions',
    titleKey: 'onboarding.missionPathTitle',
    descriptionKey: 'onboarding.missionPathDescription',
    assigned: true,
    required: true,
    detailLoaded: true,
    detailFailed: false,
    chapters: [
      {
        id: 'missions',
        index: 1,
        titleKey: 'onboarding.missionPathChapter',
        title: null,
        description: null,
        steps: leftovers.map((mission) => missionStep(mission, { required: true, prefix: 'loose:' })),
      },
    ],
  });
}

/* -------------------------------------------------------------------------- */
/* Progress + next step                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Attach completion counts. Required and total are reported separately so the
 * required half of a path can never be hidden behind voluntary depth.
 */
export function withProgress(path) {
  let total = 0;
  let done = 0;
  let requiredTotal = 0;
  let requiredDone = 0;
  const kinds = new Set();

  for (const chapter of path.chapters) {
    for (const step of chapter.steps) {
      total += 1;
      kinds.add(step.kind);
      if (step.completed) done += 1;
      if (step.required) {
        requiredTotal += 1;
        if (step.completed) requiredDone += 1;
      }
    }
  }

  return {
    ...path,
    kinds: STEP_KINDS.filter((kind) => kinds.has(kind)),
    progress: {
      total,
      completed: done,
      requiredTotal,
      requiredCompleted: requiredDone,
      percent: total > 0 ? Math.round((done / total) * 100) : 0,
      done: total > 0 && done === total,
      started: done > 0,
    },
  };
}

/** All mission ids already placed in a path — used to find the leftovers. */
export function claimedMissionIds(paths) {
  const ids = new Set();
  for (const path of paths) {
    for (const chapter of path.chapters) {
      for (const step of chapter.steps) {
        if (step.missionId) ids.add(step.missionId);
      }
    }
  }
  return ids;
}

/**
 * The one step to show as "next" (spec §9.4 "sichtbarer nächster Schritt").
 *
 * Required work outranks voluntary depth, and inside a path the order the
 * author or planner chose is respected — the next step is the first open one,
 * not the easiest one. `preferredStepId` lets a resumed session point back at
 * the step the user last touched.
 */
export function findNextStep(paths, preferredStepId = null) {
  const ordered = [...paths].sort((a, b) => Number(b.required) - Number(a.required));

  if (preferredStepId) {
    for (const path of ordered) {
      for (const chapter of path.chapters) {
        const step = chapter.steps.find((candidate) => candidate.id === preferredStepId && !candidate.completed);
        if (step) return { path, chapter, step };
      }
    }
  }

  for (const path of ordered) {
    for (const chapter of path.chapters) {
      const step = chapter.steps.find((candidate) => !candidate.completed);
      if (step) return { path, chapter, step };
    }
  }
  return null;
}
